import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Copy, Video, X } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE_URL as API_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

/**
 * Pop-up de reuniões no Office (confirmação / pedidos) + link da sala.
 */
export default function MeetingAlertsBridge() {
  const navigate = useNavigate();
  const { user, accountSummary } = useAuth();
  const { t } = useLanguage();
  const isAttendant = user?.role === 'attendant';
  const mods = Array.isArray(accountSummary?.modules) ? accountSummary.modules : [];
  const meetingsAllowed =
    isAttendant || user?.role === 'admin' || mods.includes('*') || mods.includes('meetings');
  const seenRef = useRef(new Set());
  const [active, setActive] = useState(null);

  const dismiss = useCallback(async (item) => {
    setActive(null);
    if (!item?.id) return;
    try {
      await axios.post(`${API_URL}/api/meetings/notifications/${item.id}/read`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/meetings/notifications`, {
        params: { limit: 12 },
        withCredentials: true,
      });
      const items = data?.items || [];
      const pendingPopup = items.find(
        (n) =>
          n.popup &&
          !n.read &&
          [
            'booking_confirmed',
            'booking_pending',
            'booking_cancelled',
            'booking_rescheduled',
            'booking_unforeseen',
          ].includes(n.type),
      );
      if (!pendingPopup) return;
      if (seenRef.current.has(pendingPopup.id)) return;
      seenRef.current.add(pendingPopup.id);
      setActive(pendingPopup);
    } catch {
      /* offline / sem permissão */
    }
  }, []);

  useEffect(() => {
    if (!meetingsAllowed) return undefined;
    poll();
    const id = setInterval(poll, 18000);
    const onFocus = () => poll();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [poll, meetingsAllowed]);

  if (!meetingsAllowed || !active) return null;

  const report = String(active.report || active.body || '');
  const isConfirmed = active.type === 'booking_confirmed';

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        aria-label="Fechar"
        onClick={() => dismiss(active)}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md rounded-[0.2rem] border border-border bg-card p-5 shadow-2xl"
      >
        <button
          type="button"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-[0.2rem] border border-border text-muted-foreground"
          onClick={() => dismiss(active)}
        >
          <X size={16} />
        </button>
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-[0.2rem] bg-primary/12 text-primary">
          <CheckCircle2 size={22} />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Reuniões</p>
        <h2 className="mt-1 text-lg font-semibold tracking-tight text-foreground">{active.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{active.body}</p>
        {report ? (
          <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-[0.2rem] border border-border bg-muted/30 p-3 text-[12px] leading-relaxed text-foreground">
            {report.replace(/\*/g, '')}
          </pre>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-9 gap-1.5 rounded-[0.2rem]"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(report);
                toast.success('Relatório copiado');
              } catch {
                toast.message(report);
              }
            }}
          >
            <Copy size={14} /> Copiar
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-9 rounded-[0.2rem]"
            onClick={() => {
              dismiss(active);
              navigate('/reunioes');
            }}
          >
            Ver em Reuniões
          </Button>
          {isConfirmed && active.join_path ? (
            <Button
              type="button"
              className="h-9 gap-1.5 rounded-[0.2rem]"
              onClick={() => {
                const path = active.join_path.startsWith('/')
                  ? active.join_path
                  : `/${active.join_path}`;
                dismiss(active);
                navigate(path.includes('/sala/') ? path : `/reunioes/sala/${active.booking_id}`);
              }}
            >
              <Video size={14} />{' '}
              {isAttendant ? t('meetings.homeCard.cta') : t('meetings.enterRoom')}
            </Button>
          ) : (
            <Button type="button" className="h-9 rounded-[0.2rem]" onClick={() => dismiss(active)}>
              Entendi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
